import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, Overline } from "./Reveal";
import { SERVICES, PHONE_DISPLAY, PHONE_TEL, EMAIL, EMAIL_MAILTO } from "../lib/site";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ImagePlus, X, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PHOTOS = 4;

const fieldClass =
  "rounded-none border-lec-ink/25 bg-white focus-visible:ring-2 focus-visible:ring-lec-green focus-visible:ring-offset-0";

export const QuoteForm = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", suburb: "", service: "", message: "" });
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState("idle");

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const addFiles = (e) => {
    const picked = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
    const next = [...files, ...picked.map((file) => ({ file, url: URL.createObjectURL(file) }))].slice(0, MAX_PHOTOS);
    setFiles(next);
    e.target.value = "";
  };

  const removeFile = (idx) => {
    setFiles((prev) => {
      URL.revokeObjectURL(prev[idx].url);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Please add your name so Damien knows who he's calling.");
      return;
    }
    if (!form.phone.trim() && !form.email.trim()) {
      toast.error("Please leave a phone number or email so we can reach you.");
      return;
    }
    setStatus("sending");
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach(({ file }) => fd.append("photos", file));
      await axios.post(`${API}/quote`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setStatus("done");
      toast.success("Enquiry sent — Damien will be in touch shortly.");
    } catch (err) {
      setStatus("idle");
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try calling instead.");
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="scroll-mt-20 bg-lec-bone">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-20 md:px-10 md:py-28 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <Overline>Start your project</Overline>
          <h2 className="mt-6 font-display text-4xl font-semibold tracking-tight text-lec-ink sm:text-5xl">
            Request a <span className="underline decoration-lec-green decoration-8 underline-offset-8">quote.</span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-lec-charcoal md:text-lg">
            Tell us what you're planning and attach a few photos of the area. Your enquiry lands
            straight in Damien's inbox — he'll come back to you with honest advice and a time for
            an onsite consultation.
          </p>
          <div className="mt-10 space-y-4">
            <a
              href={PHONE_TEL}
              data-testid="quote-call-alt"
              className="group flex items-center justify-between border-2 border-lec-ink bg-white px-6 py-5 transition-[background-color] duration-200 hover:bg-lec-ink"
            >
              <span className="text-sm font-bold uppercase tracking-wider text-lec-ink transition-colors duration-200 group-hover:text-lec-green">
                Prefer to talk?
              </span>
              <span className="font-display text-xl font-semibold text-lec-ink transition-colors duration-200 group-hover:text-lec-bone">
                {PHONE_DISPLAY}
              </span>
            </a>
            <a
              href={EMAIL_MAILTO}
              data-testid="quote-email-alt"
              className="text-sm font-semibold text-lec-charcoal underline decoration-lec-green decoration-2 underline-offset-4 transition-colors hover:text-lec-ink"
            >
              {EMAIL}
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7">
          {status === "done" ? (
            <div
              data-testid="quote-success-message"
              className="flex h-full flex-col items-start justify-center border-2 border-lec-ink bg-white p-10 shadow-[8px_8px_0_#49FF00]"
            >
              <CheckCircle2 className="h-12 w-12 text-lec-green" strokeWidth={2.5} />
              <h3 className="mt-6 font-display text-3xl font-semibold text-lec-ink">Enquiry sent.</h3>
              <p className="mt-4 max-w-md text-base text-lec-charcoal">
                Thanks {form.name.split(" ")[0]} — your request is in Damien's inbox
                {files.length > 0 && ` along with ${files.length} photo${files.length > 1 ? "s" : ""}`}.
                He'll be in touch within one business day.
              </p>
              <button
                data-testid="quote-send-another-button"
                onClick={() => {
                  setForm({ name: "", phone: "", email: "", suburb: "", service: "", message: "" });
                  files.forEach((f) => URL.revokeObjectURL(f.url));
                  setFiles([]);
                  setStatus("idle");
                }}
                className="mt-8 border-b-2 border-lec-green pb-1 text-sm font-bold uppercase tracking-wider text-lec-ink transition-colors hover:text-lec-green"
              >
                Send another enquiry
              </button>
            </div>
          ) : (
            <form
              data-testid="quote-form"
              onSubmit={submit}
              className="border-2 border-lec-ink bg-white p-6 shadow-[8px_8px_0_#111111] md:p-10"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <Label htmlFor="quote-name" className="text-xs font-bold uppercase tracking-[0.15em]">Name *</Label>
                  <Input id="quote-name" data-testid="quote-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={`mt-2 ${fieldClass}`} />
                </div>
                <div>
                  <Label htmlFor="quote-phone" className="text-xs font-bold uppercase tracking-[0.15em]">Phone *</Label>
                  <Input id="quote-phone" data-testid="quote-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="04XX XXX XXX" className={`mt-2 ${fieldClass}`} />
                </div>
                <div>
                  <Label htmlFor="quote-email" className="text-xs font-bold uppercase tracking-[0.15em]">Email</Label>
                  <Input id="quote-email" data-testid="quote-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={`mt-2 ${fieldClass}`} />
                </div>
                <div>
                  <Label htmlFor="quote-suburb" className="text-xs font-bold uppercase tracking-[0.15em]">Suburb</Label>
                  <Input id="quote-suburb" data-testid="quote-suburb-input" value={form.suburb} onChange={set("suburb")} placeholder="e.g. Fremantle" className={`mt-2 ${fieldClass}`} />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="quote-service" className="text-xs font-bold uppercase tracking-[0.15em]">Service</Label>
                  <select
                    id="quote-service"
                    data-testid="quote-service-select"
                    value={form.service}
                    onChange={set("service")}
                    className="mt-2 h-10 w-full rounded-none border border-lec-ink/25 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-lec-green"
                  >
                    <option value="">General enquiry</option>
                    {SERVICES.map((s) => (
                      <option key={s.title} value={s.title}>{s.title}</option>
                    ))}
                    <option value="Pool surrounds / outdoor area">Pool surrounds / outdoor area</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="quote-message" className="text-xs font-bold uppercase tracking-[0.15em]">Project details</Label>
                  <Textarea id="quote-message" data-testid="quote-message-input" rows={4} value={form.message} onChange={set("message")} placeholder="Tell us about the area — size, access, the finish you're after…" className={`mt-2 ${fieldClass}`} />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs font-bold uppercase tracking-[0.15em]">Photos of the area (up to {MAX_PHOTOS})</Label>
                  <label
                    data-testid="quote-photo-dropzone"
                    className="mt-2 flex cursor-pointer items-center justify-center gap-3 border-2 border-dashed border-lec-ink/30 px-6 py-8 text-sm font-semibold text-lec-charcoal transition-[border-color,background-color] duration-200 hover:border-lec-green hover:bg-lec-green/5"
                  >
                    <ImagePlus className="h-5 w-5" />
                    Tap to add photos — JPG, PNG or WEBP
                    <input
                      data-testid="quote-photo-input"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      multiple
                      onChange={addFiles}
                      className="hidden"
                    />
                  </label>
                  {files.length > 0 && (
                    <div className="mt-4 grid grid-cols-4 gap-3" data-testid="quote-photo-previews">
                      {files.map((f, i) => (
                        <div key={f.url} className="group relative">
                          <img src={f.url} alt={`Upload preview ${i + 1}`} className="aspect-square w-full border border-lec-line object-cover" />
                          <button
                            type="button"
                            data-testid={`quote-photo-remove-${i}`}
                            onClick={() => removeFile(i)}
                            aria-label={`Remove photo ${i + 1}`}
                            className="absolute -right-2 -top-2 bg-lec-ink p-1 text-lec-bone transition-colors hover:bg-lec-green hover:text-lec-ink"
                          >
                            <X className="h-3 w-3" strokeWidth={3} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <button
                type="submit"
                data-testid="quote-submit-button"
                disabled={status === "sending"}
                className="mt-8 flex w-full items-center justify-center gap-3 bg-lec-green px-8 py-4 text-sm font-bold uppercase tracking-wider text-lec-ink shadow-[6px_6px_0_#111111] transition-[transform,box-shadow,background-color,color] duration-200 hover:-translate-y-0.5 hover:bg-lec-ink hover:text-lec-green hover:shadow-[8px_8px_0_#49FF00] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === "sending" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                  </>
                ) : (
                  "Request a Quote"
                )}
              </button>
              <p className="mt-4 text-center text-xs text-lec-charcoal">
                Goes straight to Damien's inbox. No spam, no obligation.
              </p>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
